import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Pagination from "@ui/pagination/pagination";
import { SwipeToSeeMore } from "@ui/swipe/swipe";

import { Loader } from "@ui/loader/loader";
import { FundsMainNavBar } from "./funds-main-search-bar/funds-main-nav-bar";
import { FundsMainSearchBar } from "./funds-main-search-bar/funds-main-search-bar";
import { FundsTable } from "./funds-table/funds-table";
import { useFunds } from "@/providers/bookmarks-provider/hooks/use-fund";
import { useOverviewFilterValues } from "@/providers/overview-filters-provider/hooks/use-overview-filter-values";
import { useOverviewFilterHandlers } from "@/providers/overview-filters-provider/hooks/use-overview-filter-handlers";
import {
  SORT_ORDER_OPTIONS,
  SortOrderValue,
} from "@/hooks/use-table-sort/use-table-sort";

const perPage = 10;

export type Filters = {
  sortBy: string;
  sortOrder: SortOrderValue | "";
  searchValue: string;
  onlyVerified: boolean;
  totalLockedRange: number[];
  priceChangeRange: number[];
  activePriceRadio: string;
  feeRange: number[];
};
export const FILTER_KEYS = {
  search: "searchValue",
  verified: "onlyVerified",
  locked: "totalLockedRange",
  priceChange: "priceChangeRange",
  priceRadio: "activePriceRadio",
  fee: "feeRange",
} as const;
export type FilterKeysValue = typeof FILTER_KEYS[keyof typeof FILTER_KEYS];

const SORT_TO_FILTERS = {
  name: "fund_name",
  price: "fund_price",
  ["change-daily"]: "price_change24h",
  ["total-locked"]: "total_locked",
  inflow: "inflow_7d",
  outflow: "outflow_7d",
  ["fund-fee"]: "fund_fee",
};

export const FundsMain = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(() =>
    searchParams.has("page") ? Number(searchParams.get("page")) : 1,
  );

  const { funds, isFundsLoading } = useFunds();

  const { filters, maxLockedRange } = useOverviewFilterValues();
  const { updateFilterField } = useOverviewFilterHandlers();

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const { displayList, totalPages } = useMemo(() => {
    let newFunds = [...funds];

    // Decode hex names for search/display
    // Fund name structure: first 16 hex chars (8 bytes) = fund name, rest is hash
    const fundsWithDecodedNames = newFunds.map(fund => {
      const decodeHex = (hex: string) => {
        try {
          // Take only first 16 hex characters (8 bytes) for the fund name
          const nameHex = hex.substring(0, 16);
          let str = '';
          for (let i = 0; i < nameHex.length; i += 2) {
            str += String.fromCharCode(parseInt(nameHex.substring(i, i + 2), 16));
          }
          return str;
        } catch {
          return hex;
        }
      };
      return {
        ...fund,
        decodedName: decodeHex(fund.name)
      };
    });

    // Search by decoded name
    if (filters.searchValue.trim() !== "") {
      newFunds = fundsWithDecodedNames
        .filter((fund) =>
          fund.decodedName
            .toLowerCase()
            .includes(filters.searchValue.toLowerCase().trim()),
        );
    }

    // Sort by name only (fee sorting removed for now since royalty_factor/fund_factor calc is complex)
    if (!!filters.sortOrder && filters.sortOrder && filters.sortBy === "name") {
      const decoded = newFunds.map(fund => {
        const decodeHex = (hex: string) => {
          try {
            // Take only first 16 hex characters (8 bytes) for the fund name
            const nameHex = hex.substring(0, 16);
            let str = '';
            for (let i = 0; i < nameHex.length; i += 2) {
              str += String.fromCharCode(parseInt(nameHex.substring(i, i + 2), 16));
            }
            return str;
          } catch {
            return hex;
          }
        };
        return { ...fund, decodedName: decodeHex(fund.name) };
      });

      decoded.sort((fund_1, fund_2) =>
        filters.sortOrder === SORT_ORDER_OPTIONS.ASC
          ? fund_1.decodedName.localeCompare(fund_2.decodedName)
          : fund_2.decodedName.localeCompare(fund_1.decodedName),
      );
      newFunds = decoded;
    }

    return {
      displayList: newFunds.slice(
        currentPage === 1 ? 0 : perPage * (currentPage - 1),
        currentPage === 1 ? perPage : perPage * currentPage,
      ),
      totalPages: Math.ceil(newFunds.length / perPage),
    };
  }, [currentPage, filters, funds]);

  return (
    <section className="mt-6 p-12px md:px-24px md:py-32px w-11/12 md:w-full bg-white dark:bg-dark-gray900 rounded-lg overflow-hidden md:overflow-visible">
      <FundsMainNavBar />
      <FundsMainSearchBar
        maxLocked={maxLockedRange}
        handleFilters={updateFilterField}
        filters={filters}
        // todo: fix mobile setFilters
        setFilters={(values) => console.log("mob filters", values)}
      />
      <div className="w-full">
        {isFundsLoading ? (
          <Loader />
        ) : (
          <div className="w-full">
            <div className="w-full overflow-x-scroll md:overflow-x-auto pb-3 md:pb-0">
              <FundsTable data={displayList} />
            </div>
            <SwipeToSeeMore />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </section>
  );
};
